import { useEffect, useRef, useState } from "react";
import erzieherinKinder from "@/assets/musik-kita/erzieherin-kinder.png";

const TransformationSection = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !containerRef.current) return;

      const sectionRect = sectionRef.current.getBoundingClientRect();
      const containerHeight = containerRef.current.offsetHeight;
      const windowHeight = window.innerHeight;

      // Calculate scroll progress within the section
      // Progress from 0 (section enters viewport) to 1 (section exits viewport)
      const scrollStart = -sectionRect.top;
      const scrollRange = containerHeight - windowHeight;
      const progress = Math.max(0, Math.min(1, scrollStart / scrollRange));

      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate opacity values based on scroll progress
  const colorOpacity = scrollProgress;
  const chaosTextOpacity = 1 - scrollProgress * 2; // Fades out faster
  const clarityTextOpacity = (scrollProgress - 0.3) * 2; // Fades in later

  return (
    <section ref={sectionRef} className="relative">
      {/* Spacer to create scroll distance */}
      <div ref={containerRef} className="h-[200vh]">
        {/* Sticky container */}
        <div className="sticky top-0 overflow-hidden">
          <div className="w-full">
            <div className="w-full overflow-hidden">
              {/* Image container */}
              <div className="relative h-screen w-full">
                {/* Grayscale base image with blur */}
                <div className="absolute inset-0">
                  <img
                    src={erzieherinKinder}
                    alt="Chaos"
                    className="w-full h-full object-cover grayscale blur-sm"
                  />
                </div>

                {/* Color image overlay - fades in on scroll */}
                <div
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{ opacity: colorOpacity }}
                >
                  <img
                    src={erzieherinKinder}
                    alt="Klarheit"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Dark overlay for better text readability - reduces as color appears */}
                <div 
                  className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 transition-opacity duration-300"
                  style={{ opacity: 1 - colorOpacity * 0.4 }}
                />
              </div>

              {/* Content overlays */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full px-6 md:px-12">
                  {/* Chaos text - fades out */}
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-500"
                    style={{ opacity: chaosTextOpacity }}
                  >
                    <div className="text-center space-y-4 md:space-y-6">
                      <div className="inline-block px-5 py-2 bg-destructive/90 backdrop-blur-sm rounded-full mb-2">
                        <span className="text-xs md:text-sm font-bold text-white uppercase tracking-wide">
                          Das Problem
                        </span>
                      </div>
                      <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 drop-shadow-lg">
                        Keine Zeit, kein Plan, keine Ideen
                      </h2>
                      <div className="space-y-2 md:space-y-3 max-w-xl mx-auto">
                        <p className="text-lg md:text-xl text-white/80 font-medium">
                          ❌ Jeden Tag die gleichen Lieder
                        </p>
                        <p className="text-lg md:text-xl text-white/80 font-medium">
                          ❌ Unsicher an Gitarre & Instrumenten
                        </p>
                        <p className="text-lg md:text-xl text-white/80 font-medium">
                          ❌ Vorbereitung kostet zu viel Zeit
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Clarity text - fades in */}
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-500"
                    style={{ opacity: clarityTextOpacity }}
                  >
                    <div className="text-center space-y-4 md:space-y-6">
                      <div className="inline-block px-5 py-2 bg-gradient-to-r from-primary to-accent backdrop-blur-sm rounded-full mb-2 shadow-lg">
                        <span className="text-xs md:text-sm font-bold text-white uppercase tracking-wide">
                          Die Lösung
                        </span>
                      </div>
                      <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 drop-shadow-lg">
                        Endlich entspannt musizieren
                      </h2>
                      <div className="space-y-2 md:space-y-3 max-w-xl mx-auto">
                        <p className="text-lg md:text-xl text-white font-bold drop-shadow-md">
                          ✓ Fertige Konzepte, direkt einsetzbar
                        </p>
                        <p className="text-lg md:text-xl text-white font-bold drop-shadow-md">
                          ✓ Video-Anleitungen Schritt für Schritt
                        </p>
                        <p className="text-lg md:text-xl text-white font-bold drop-shadow-md">
                          ✓ Sofort umsetzbar, ohne Vorbereitung
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scroll indicator */}
              <div 
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs md:text-sm animate-bounce transition-opacity duration-300"
                style={{ opacity: 1 - scrollProgress }}
              >
                Scroll weiter ↓
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection;
